import { useRef, useCallback, useEffect, useState } from 'react';
import type { LandingSection } from '@easyflow/shared';

const MAX_HISTORY = 30;

export function useUndoRedo(
  sections: LandingSection[],
  setSections: React.Dispatch<React.SetStateAction<LandingSection[]>>,
  pageId?: string | null
) {
  const historyRef = useRef<LandingSection[][]>([]);
  const historyIndexRef = useRef<number>(-1);
  const skipPushRef = useRef(false);
  const activePageIdRef = useRef<string | null | undefined>(undefined);
  const pageResetPendingRef = useRef(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const bumpVersion = useCallback(() => setHistoryVersion(v => v + 1), []);

  const resetHistory = useCallback((initialSections: LandingSection[]) => {
    historyRef.current = [JSON.parse(JSON.stringify(initialSections))];
    historyIndexRef.current = 0;
    skipPushRef.current = false;
    pageResetPendingRef.current = false;
    bumpVersion();
  }, [bumpVersion]);

  useEffect(() => {
    if (activePageIdRef.current !== pageId) {
      activePageIdRef.current = pageId;
      pageResetPendingRef.current = true;
    }
  }, [pageId]);

  useEffect(() => {
    if (pageResetPendingRef.current) {
      resetHistory(sections);
      return;
    }
  }, [sections, pageId, resetHistory]);

  const pushHistory = useCallback((snapshot: LandingSection[]) => {
    if (skipPushRef.current) {
      skipPushRef.current = false;
      return;
    }
    if (pageResetPendingRef.current) return;
    const currentIdx = historyIndexRef.current;
    const currentSnapshot = historyRef.current[currentIdx];
    if (currentSnapshot && JSON.stringify(currentSnapshot) === JSON.stringify(snapshot)) {
      return;
    }
    historyRef.current = historyRef.current.slice(0, currentIdx + 1);
    historyRef.current.push(JSON.parse(JSON.stringify(snapshot)));
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.shift();
    }
    historyIndexRef.current = historyRef.current.length - 1;
    bumpVersion();
  }, [bumpVersion]);

  useEffect(() => {
    if (!pageResetPendingRef.current) {
      pushHistory(sections);
    }
  }, [sections, pushHistory]);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    skipPushRef.current = true;
    setSections(JSON.parse(JSON.stringify(snapshot)));
    bumpVersion();
  }, [setSections, bumpVersion]);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current += 1;
    const snapshot = historyRef.current[historyIndexRef.current];
    skipPushRef.current = true;
    setSections(JSON.parse(JSON.stringify(snapshot)));
    bumpVersion();
  }, [setSections, bumpVersion]);

  const canUndo = historyVersion >= 0 && historyIndexRef.current > 0;
  const canRedo = historyVersion >= 0 && historyIndexRef.current < historyRef.current.length - 1;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z') || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  return { undo, redo, canUndo, canRedo, resetHistory };
}
